import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { formatDate } from '@utils/format';
import { useUpdateMarketPrice } from '../hooks';
import {
  marketPriceEditFormSchema,
  type MarketPrice,
  type MarketPriceEditFormValues,
} from '../schema';

interface Props {
  open: boolean;
  row: MarketPrice | null;
  onClose: () => void;
}

// Inputs hold strings (HTML <input type="number"> still gives strings via RHF
// when handled this way). We coerce via the schema's `preprocess` step on
// submit, so this default keeps the UI in sync with what the editor sees.
function valueToInput(v: number | null): string {
  return v == null ? '' : String(v);
}

function buildDefaults(row: MarketPrice | null): {
  minPrice: string;
  modalPrice: string;
  maxPrice: string;
  commodityArrivals: string;
} {
  return {
    minPrice: valueToInput(row?.minPrice ?? null),
    modalPrice: valueToInput(row?.modalPrice ?? null),
    maxPrice: valueToInput(row?.maxPrice ?? null),
    commodityArrivals: valueToInput(row?.commodityArrivals ?? null),
  };
}

export function MarketPriceEditDialog({ open, row, onClose }: Props) {
  const updateMutation = useUpdateMarketPrice();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MarketPriceEditFormValues>({
    // The schema's preprocess turns the string inputs into number | null on
    // validate; the form values type after parse is MarketPriceEditFormValues.
    resolver: zodResolver(marketPriceEditFormSchema) as Resolver<MarketPriceEditFormValues>,
    defaultValues: buildDefaults(null) as unknown as MarketPriceEditFormValues,
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  // Re-seed defaults whenever the dialog opens for a different row.
  useEffect(() => {
    if (!open || !row) return;
    reset(buildDefaults(row) as unknown as MarketPriceEditFormValues);
  }, [open, row, reset]);

  const onSubmit = async (values: MarketPriceEditFormValues) => {
    if (!row) return;
    try {
      await updateMutation.mutateAsync({
        id: row.id,
        payload: {
          minPrice: values.minPrice,
          modalPrice: values.modalPrice,
          maxPrice: values.maxPrice,
          commodityArrivals: values.commodityArrivals,
        },
      });
      onClose();
    } catch {
      // Toast already shown by the mutation's onError.
    }
  };

  const submitting = isSubmitting || updateMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="sm"
      fullWidth
      aria-labelledby="market-price-edit-title"
    >
      <DialogTitle id="market-price-edit-title" sx={{ pb: 1 }}>
        Edit market price
      </DialogTitle>
      <DialogContent dividers>
        {row && (
          <>
            <div className="mb-4 grid grid-cols-3 gap-3 rounded-lg bg-surface-muted px-4 py-3 text-[12.5px]">
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Date
                </div>
                <div className="font-medium text-foreground">
                  {formatDate(row.priceDate)}
                </div>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Crop / Variety
                </div>
                <div className="font-medium text-foreground">
                  {row.cropName}
                </div>
                <div className="text-foreground-muted">{row.varietyName}</div>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Mandi
                </div>
                <div className="font-medium text-foreground">
                  {row.apmcName}
                </div>
              </div>
            </div>

            <form
              id="market-price-edit-form"
              onSubmit={handleSubmit(onSubmit)}
              className="grid grid-cols-2 gap-4"
            >
              <TextField
                label={`Min (₹/${row.unit})`}
                size="small"
                type="number"
                slotProps={{ htmlInput: { step: '0.01', min: '0', inputMode: 'decimal' } }}
                fullWidth
                {...register('minPrice')}
                error={!!errors.minPrice}
                helperText={errors.minPrice?.message ?? ' '}
              />
              <TextField
                label={`Modal (₹/${row.unit})`}
                size="small"
                type="number"
                slotProps={{ htmlInput: { step: '0.01', min: '0', inputMode: 'decimal' } }}
                fullWidth
                {...register('modalPrice')}
                error={!!errors.modalPrice}
                helperText={errors.modalPrice?.message ?? ' '}
              />
              <TextField
                label={`Max (₹/${row.unit})`}
                size="small"
                type="number"
                slotProps={{ htmlInput: { step: '0.01', min: '0', inputMode: 'decimal' } }}
                fullWidth
                {...register('maxPrice')}
                error={!!errors.maxPrice}
                helperText={errors.maxPrice?.message ?? ' '}
              />
              <TextField
                label={`Arrivals (${row.unit})`}
                size="small"
                type="number"
                slotProps={{ htmlInput: { step: '0.01', min: '0', inputMode: 'decimal' } }}
                fullWidth
                {...register('commodityArrivals')}
                error={!!errors.commodityArrivals}
                helperText={errors.commodityArrivals?.message ?? ' '}
              />
            </form>

            <p className="mt-2 text-[12px] text-foreground-muted">
              All four fields are required. Min ≤ Modal ≤ Max is enforced on save.
            </p>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="market-price-edit-form"
          variant="contained"
          color="primary"
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
